import { useForm } from '@inertiajs/react'
import { KeyRound, LogIn } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { FieldError } from '@/components/feedback/FieldError'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Button } from '@/components/ui/Button'

type LoginProps = {
  defaults?: {
    email: string
    password: string
  }
}

export default function PlayerLogin({ defaults }: LoginProps) {
  const { data, setData, post, processing, errors, transform } = useForm({
    email: defaults?.email ?? '',
    password: defaults?.password ?? '',
  })

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    transform((current) => ({ player: current }))
    post('/player/login')
  }

  // Type assertion for base error which comes from the server
  const baseError = (errors as { base?: string }).base

  return (
    <AppShell>
      <div className="flex flex-col gap-10">
        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-500/20 text-brand-100">
            <KeyRound className="h-6 w-6" />
          </div>
          <h1 className="mt-4 text-3xl font-semibold text-white">選手ログイン</h1>
          <p className="mt-2 text-sm text-white/60">
            Talentboxの選手専用ダッシュボードへログインして、学校コーチとのやり取りを始めましょう。
          </p>
        </div>

        <form onSubmit={submit} className="space-y-6">
          {baseError ? (
            <div className="rounded-2xl border border-danger-400/30 bg-danger-500/10 px-4 py-3 text-sm text-danger-100">
              {baseError}
            </div>
          ) : null}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">メールアドレス</Label>
              <Input
                id="email"
                type="email"
                value={data.email}
                onChange={(event) => setData('email', event.target.value)}
                placeholder="player@example.com"
                autoComplete="email"
              />
              <FieldError message={errors.email} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">パスワード</Label>
              <Input
                id="password"
                type="password"
                value={data.password}
                onChange={(event) => setData('password', event.target.value)}
                autoComplete="current-password"
              />
              <FieldError message={errors.password} />
            </div>
          </div>

          <Button type="submit" loading={processing} className="w-full justify-center">
            <LogIn className="mr-2 h-4 w-4" /> ログイン
          </Button>
        </form>

        <p className="text-center text-sm">
          <a href="/player/password_resets/new" className="font-semibold text-brand-200 hover:text-brand-100">
            パスワードを忘れた方
          </a>
        </p>

        <p className="text-center text-xs text-white/40">
          アカウントに関するお問い合わせはチームコーチまたはTalentboxサポートまでご連絡ください。
        </p>
      </div>
    </AppShell>
  )
}
